import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X, Save, Calendar as CalendarIcon, User as UserIcon } from 'lucide-react';
import type { Technician } from '@shared/types';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, isWeekend, getDay } from 'date-fns';
import { fr } from 'date-fns/locale';

interface EditTechnicianModalProps {
    technician: Technician;
    isOpen: boolean;
    onClose: () => void;
    onSave: (id: string, updates: Partial<Technician>) => void;
}

export const EditTechnicianModal: React.FC<EditTechnicianModalProps> = ({
    technician,
    isOpen,
    onClose,
    onSave,
}) => {
    const [name, setName] = useState(technician.name);
    const [service, setService] = useState(technician.service);
    const [color, setColor] = useState(technician.color);

    // Calendar state
    const [viewDate, setViewDate] = useState(new Date());
    const [overrides, setOverrides] = useState(technician.overrides || {});

    // Tab state
    const [activeTab, setActiveTab] = useState<'profile' | 'planning'>('profile');

    const handleSaveProfile = () => {
        onSave(technician.id, { name, service, color });
    };

    const toggleDay = (date: Date) => {
        const key = format(date, 'yyyy-MM-dd');
        const newOverrides = { ...overrides };

        if (newOverrides[key] === 'vacation') {
            delete newOverrides[key];
        } else {
            newOverrides[key] = 'vacation';
        }

        setOverrides(newOverrides);
        onSave(technician.id, { overrides: newOverrides });
    };

    const renderCalendar = () => {
        const start = startOfMonth(viewDate);
        const end = endOfMonth(viewDate);
        const days = eachDayOfInterval({ start, end });

        // Pad with empty cells for start of month
        const startDay = getDay(start); // 0 = Sunday
        const padding = startDay === 0 ? 6 : startDay - 1; // Mon = 0 in our grid

        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <button onClick={() => setViewDate(addMonths(viewDate, -1))} className="p-1 hover:bg-white/10 rounded">&lt;</button>
                    <span className="font-semibold capitalize">{format(viewDate, 'MMMM yyyy', { locale: fr })}</span>
                    <button onClick={() => setViewDate(addMonths(viewDate, 1))} className="p-1 hover:bg-white/10 rounded">&gt;</button>
                </div>

                <div className="grid grid-cols-7 gap-1 text-center text-xs text-muted mb-2">
                    <div>L</div><div>M</div><div>M</div><div>J</div><div>V</div><div>S</div><div>D</div>
                </div>

                <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: padding }).map((_, i) => (
                        <div key={`pad-${i}`} />
                    ))}
                    {days.map(day => {
                        const dateKey = format(day, 'yyyy-MM-dd');
                        const isVacation = overrides[dateKey] === 'vacation';
                        const isWknd = isWeekend(day);

                        return (
                            <button
                                key={dateKey}
                                onClick={() => toggleDay(day)}
                                className={`
                                    h-8 rounded-lg text-sm flex items-center justify-center transition-colors
                                    ${isVacation
                                        ? 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/50'
                                        : isWknd
                                            ? 'text-muted bg-glass-border/10'
                                            : 'hover:bg-glass-border/20 text-muted hover:text-main'}
                                `}
                            >
                                {format(day, 'd')}
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center gap-2 text-xs text-muted mt-4">
                    <div className="w-3 h-3 rounded bg-yellow-500/20 border border-yellow-500/50"></div>
                    <span>Congés / Indisponible</span>
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-panel w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
                {/* Header */}
                <div className="p-6 border-b border-glass-border flex items-center justify-between bg-black/5">
                    <h2 className="text-xl font-bold text-main flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm" style={{ backgroundColor: color }}>
                            {name.charAt(0)}
                        </div>
                        Modifier {name}
                    </h2>
                    <button onClick={onClose} className="text-muted hover:text-main transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-glass-border">
                    <button
                        onClick={() => setActiveTab('profile')}
                        className={`flex-1 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'profile' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted hover:text-main'
                            }`}
                    >
                        <UserIcon size={16} className="inline mr-2" />
                        Profil
                    </button>
                    <button
                        onClick={() => setActiveTab('planning')}
                        className={`flex-1 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'planning' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted hover:text-main'
                            }`}
                    >
                        <CalendarIcon size={16} className="inline mr-2" />
                        Absences
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto custom-scrollbar">
                    {activeTab === 'profile' && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-muted mb-1">Nom</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="glass-input w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-muted mb-1">Service</label>
                                <input
                                    type="text"
                                    value={service}
                                    onChange={(e) => setService(e.target.value)}
                                    className="glass-input w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-muted mb-1">Couleur</label>
                                <div className="flex gap-2 flex-wrap">
                                    {['#f472b6', '#22d3ee', '#a78bfa', '#fbbf24', '#34d399', '#f87171', '#60a5fa', '#c084fc'].map(c => (
                                        <button
                                            key={c}
                                            onClick={() => setColor(c)}
                                            className={`w-8 h-8 rounded-full border-2 transition-transform ${color === c ? 'border-white scale-110' : 'border-transparent hover:scale-105'}`}
                                            style={{ backgroundColor: c }}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div className="pt-4">
                                <button onClick={handleSaveProfile} className="glass-button w-full bg-primary/20 hover:bg-primary/30 text-primary border-primary/50">
                                    <Save size={18} className="mr-2 inline" />
                                    Enregistrer les modifications
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'planning' && (
                        <div>
                            <p className="text-sm text-muted mb-4">
                                Cliquez sur une date pour marquer/démarquer une absence.
                            </p>
                            {renderCalendar()}
                        </div>
                    )}
                </div>
            </motion.div>
        </div>,
        document.body
    );
};
